<script setup>
import { ref, nextTick, watch } from "vue";
import Avatar from "../media/Avatar.svg";
import { useChatBot } from "../composables/useChatBot.js";
import { useChatbotUI } from "@/components/ChatBot/composables/useChatBotUI";
import chatBotApiService from "../services/chatBot.service.js";
import {
  getMessageBlocks,
  getNestedContent
} from "@/components/ChatBot/utility/helpers";

// ChatBot Composable
const {
  messages,
  isLoading,
  isStreaming,
  clearLoadingVariables,
  updateStreamingMessage,
  sendMessage: sendChatMessage
} = useChatBot();

// Chatbot controls
const { isOpen, closeChatbot } = useChatbotUI();

const newMessage = ref("");
const messagesContainer = ref(null);
const textareaRef = ref(null);
const isWindowExtended = ref(false);
const currentAbortController = ref(null);

const abortMessage = () => {
  chatBotApiService.cancelStream(currentAbortController.value);
  clearLoadingVariables();
  updateStreamingMessage("", true);
  currentAbortController.value = null;
};

const sendMessage = async () => {
  if (newMessage.value.trim() && !isLoading.value && !isStreaming.value) {
    const messageText = newMessage.value;
    newMessage.value = "";

    // Reset textarea height after sending
    nextTick(() => {
      scrollToBottom();
      adjustTextareaHeight();
    });

    // Send message and use AbortController for cancellation
    currentAbortController.value = new AbortController();
    await sendChatMessage(messageText, currentAbortController.value);
    currentAbortController.value = null;
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const handleKeyPress = event => {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    sendMessage();
  }
};

const adjustTextareaHeight = () => {
  if (textareaRef.value) {
    textareaRef.value.style.height = "";
    const newHeight = Math.min(textareaRef.value.scrollHeight, 80);
    textareaRef.value.style.height = `${newHeight}px`;
  }
};

function toggleWindowSize() {
  isWindowExtended.value = !isWindowExtended.value;
}

watch(
  isOpen,
  () => {
    if (isOpen.value && textareaRef.value) {
      textareaRef.value.focus();
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="chatbot-container">
    <!-- Chatbot Window -->
    <div
      v-show="isOpen"
      class="chatbot"
      :class="{ 'is-maximized': isWindowExtended }"
    >
      <!-- Header -->
      <header class="chatbot__header">
        <div class="chatbot__status">
          <div class="chatbot__status-indicator"></div>
          <h2 class="chatbot__title">{{ $t("chatBot.title") }}</h2>
        </div>
        <div class="chatbot__actions">
          <button
            class="btn btn-icon btn-xs chatbot__toggle-btn"
            @click="toggleWindowSize"
          >
            <i
              class="fa-light fa-flip-horizontal"
              :class="
                isWindowExtended
                  ? 'fa-arrow-down-left-and-arrow-up-right-to-center'
                  : 'fa-arrow-up-right-and-arrow-down-left-from-center'
              "
            ></i>
          </button>
          <button
            class="btn btn-icon btn-xs chatbot__close-btn"
            @click="closeChatbot"
          >
            <i class="fa-light fa-xmark"></i>
          </button>
        </div>
      </header>

      <!-- Messages Container -->
      <main class="chatbot__main">
        <div ref="messagesContainer" class="chatbot__messages">
          <div class="chatbot__timestamp">
            {{ $t("chatBot.today") }}
            {{
              new Date().toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit"
              })
            }}
          </div>

          <div
            v-for="message in messages"
            :key="message.id"
            class="chatbot__message-wrapper"
            :class="`chatbot__message-wrapper--${message.sender}`"
          >
            <!-- Avatar für Bot-Nachrichten -->
            <div v-if="message.sender === 'bot'" class="chatbot__avatar">
              <img
                :src="Avatar"
                class="chatbot__avatar-image"
                :alt="$t('chatBot.avatar')"
              />
            </div>
            <div
              class="chatbot__message"
              :class="[
                `chatbot__message--${message.sender}`,
                { 'chatbot__message--streaming': !message.isComplete }
              ]"
            >
              <div
                v-if="message.isHtml && message.sender === 'bot'"
                class="chatbot__message-html"
              >
                <!--      Separate divs for block and list elements to create an animation      -->
                <div
                  v-for="(block, index) in getMessageBlocks(message.text)"
                  :key="index"
                  class="message-block"
                >
                  <!--     Render <li> one by one for animation purposes      -->
                  <template v-if="getNestedContent(block)">
                    <component
                      :is="block.trim().startsWith('<ol') ? 'ol' : 'ul'"
                      class="list-wrapper"
                    >
                      <li
                        v-for="(liContent, liIndex) in getNestedContent(block)"
                        :key="liIndex"
                        class="list-item-anim"
                        v-html="liContent"
                      ></li>
                    </component>
                  </template>
                  <template v-else>
                    <div v-html="block"></div>
                  </template>
                </div>

                <div v-if="!message.isComplete" class="stream-cursor"></div>
              </div>
              <p v-else class="chatbot__message-text">{{ message.text }}</p>

              <div
                v-if="
                  isLoading && !message.isComplete && message.sender === 'bot'
                "
                class="chatbot__typing-indicator"
              >
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- Input Area -->
      <footer class="chatbot__footer">
        <div class="chatbot__input-container">
          <textarea
            ref="textareaRef"
            v-model="newMessage"
            class="chatbot__input"
            :placeholder="$t('chatBot.inputPlaceholder')"
            autofocus
            @keypress="handleKeyPress"
            @input="adjustTextareaHeight"
          ></textarea>
          <button
            v-if="isLoading || isStreaming"
            class="btn btn-primary btn-sm chatbot__abort"
            @click="abortMessage"
          >
            <span class="chatbot__abort-icon" />
          </button>
          <button
            v-else
            class="btn btn-primary btn-sm chatbot__submit"
            :disabled="!newMessage.trim()"
            @click="sendMessage"
          >
            <i class="fal fa-arrow-right icon-md" />
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
// === SCSS VARIABLES ===
$chatbot-primary-color: #5b64ee;
$chatbot-secondary-color: #f0f1f3;
$chatbot-white-color: #ffffff;
$chatbot-text-primary: #000000;
$chatbot-text-secondary: #718096;
$chatbot-text-light: #a0aec0;
$chatbot-background: $chatbot-white-color;
$chatbot-border: #c9cdd4;
$chatbot-success-color: #10b981;
$chatbot-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
$chatbot-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
$chatbot-radius: 12px;
$chatbot-radius-sm: 8px;
$chatbot-spacing-xs: 0.25rem;
$chatbot-spacing-sm: 0.5rem;
$chatbot-spacing-md: 1rem;
$chatbot-spacing-lg: 1.5rem;
$chatbot-font-size-sm: 0.875rem;
$chatbot-font-size-base: 1rem;
$chatbot-font-size-lg: 1.125rem;
$chatbot-transition: all 0.2s ease-in-out;

// === MIXINS ===
@mixin flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@mixin flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@mixin scroll-style {
  scrollbar-width: thin;
  scrollbar-color: $chatbot-border transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: $chatbot-border;
    border-radius: 3px;

    &:hover {
      background: $chatbot-text-light;
    }
  }
}

// === CONTAINER ===
.chatbot-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

// === MAIN COMPONENT ===
.chatbot {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  min-width: 400px;
  height: 600px;
  background: $chatbot-background;
  border: 1px solid $chatbot-border;
  border-radius: $chatbot-radius;
  box-shadow: $chatbot-shadow-lg;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
  animation: slideUp 0.3s ease-out;

  &.is-maximized {
    width: 600px;
    min-width: 600px;
    height: 800px;
  }
}

// === HEADER ===
.chatbot__header {
  @include flex-between;
  padding: $chatbot-spacing-md $chatbot-spacing-lg;
  background: $chatbot-background;
  border-bottom: 1px solid $chatbot-border;
  flex-shrink: 0;
}

.chatbot__status {
  @include flex-center;
  gap: $chatbot-spacing-sm;

  &-indicator {
    width: 8px;
    height: 8px;
    background: $chatbot-success-color;
    border-radius: 50%;
    flex-shrink: 0;
  }
}

.chatbot__actions {
  display: flex;
  align-items: center;
  gap: $chatbot-spacing-sm;

  .chatbot__close-btn,
  .chatbot__toggle-btn {
    background: #f2f4f7;

    i {
      color: $chatbot-text-primary;
    }
  }
}

.chatbot__title {
  font-size: $chatbot-font-size-lg;
  font-weight: 600;
  color: $chatbot-text-primary;
  margin: 0;
}

// === MAIN CONTENT ===
.chatbot__main {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chatbot__messages {
  flex: 1;
  overflow-y: auto;
  padding: $chatbot-spacing-md;
  @include scroll-style;
}

.chatbot__timestamp {
  text-align: center;
  color: $chatbot-text-light;
  font-size: $chatbot-font-size-sm;
  margin-bottom: $chatbot-spacing-lg;
  padding: $chatbot-spacing-sm 0;
}

// === MESSAGES ===
.chatbot__message-wrapper {
  margin-bottom: $chatbot-spacing-md;

  &--user {
    display: flex;
    justify-content: flex-end;
  }

  &--bot {
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    gap: $chatbot-spacing-sm;
  }
}

.chatbot__avatar {
  flex-shrink: 0;
  margin-bottom: $chatbot-spacing-xs;

  &-image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
}

.chatbot__message {
  max-width: 80%;
  padding: $chatbot-spacing-sm $chatbot-spacing-md;
  border-radius: $chatbot-radius;
  position: relative;
  word-wrap: break-word;

  &--user {
    background: $chatbot-primary-color;
    color: $chatbot-white-color;
    border-bottom-right-radius: 4px;
  }

  &--bot {
    background: $chatbot-secondary-color;
    color: $chatbot-text-primary;
    border-bottom-left-radius: 4px;
    max-width: calc(80% - 40px);
  }

  &-text {
    margin: 0;
    font-size: $chatbot-font-size-base;
    line-height: 1.4;
  }

  &-html {
    margin: 0;
    font-size: $chatbot-font-size-base;
    line-height: 1.4;

    // HTML-Element-Styling uses :deep() because of v-html
    :deep(p) {
      margin: 0 0 $chatbot-spacing-sm 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul, ol) {
      margin: $chatbot-spacing-sm 0;
      padding-left: $chatbot-spacing-lg;

      li {
        margin-bottom: $chatbot-spacing-xs;
      }
    }

    :deep(h3) {
      font-size: $chatbot-font-size-lg;
      font-weight: 600;
      margin: $chatbot-spacing-md 0 $chatbot-spacing-sm 0;
      color: $chatbot-text-primary;
    }

    :deep(strong) {
      font-weight: 600;
      color: $chatbot-text-primary;
    }

    :deep(a) {
      color: $chatbot-primary-color;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &--streaming {
    position: relative;

    .chatbot__message-text {
      padding-right: 2rem;
    }
  }
}

.chatbot__typing-indicator {
  display: inline-flex;
  gap: 2px;
  margin-left: $chatbot-spacing-xs;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: $chatbot-text-light;
    animation: typing 1.4s infinite;

    &:nth-child(1) {
      animation-delay: 0s;
    }
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

// === FOOTER ===
.chatbot__footer {
  padding: $chatbot-spacing-md $chatbot-spacing-lg;
  background: $chatbot-background;
  border-top: 1px solid $chatbot-border;
  flex-shrink: 0;
}

.chatbot__input-container {
  display: flex;
  align-items: flex-end;
  gap: $chatbot-spacing-sm;
  background: $chatbot-secondary-color;
  border-radius: $chatbot-radius;
  padding: $chatbot-spacing-sm;
  transition: $chatbot-transition;

  &:focus-within {
    box-shadow: 0 0 0 3px rgba(79, 117, 255, 0.1);
  }
}

.chatbot__input {
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-size: $chatbot-font-size-base;
  color: $chatbot-text-primary;
  font-family: inherit;
  line-height: 1.4;
  min-height: 20px;
  max-height: 80px;
  overflow-y: auto;
  transition: height 0.1s ease;

  &::placeholder {
    color: $chatbot-text-light;
  }

  // Ensure proper scrolling when max height is reached
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: $chatbot-text-light;
    border-radius: 2px;
  }
}

.chatbot__submit,
.chatbot__abort {
  i {
    color: $chatbot-white-color;
    padding: 0;
  }
}

.message-block {
  animation: fadeSlideIn 0.5s ease-out forwards;
  opacity: 0;
}

.list-item-anim {
  animation: fadeSlideIn 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;

  opacity: 0;
  transform: translateY(10px);
}

.list-wrapper {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  animation: none !important;

  &[start] {
    list-style-type: decimal;
  }
}

// Use a simple square button for abort because Fontawesome doesn't have a suitable icon
.chatbot__abort {
  height: -webkit-fill-available;

  &-icon {
    display: block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: $chatbot-white-color;
  }
}

// === ANIMATIONS ===
.chatbot__message-wrapper {
  animation: slideIn 0.3s ease-out;
}

@keyframes typing {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeSlideIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
